import { Injectable, Injector } from '@angular/core';
import {
  RepositoryAbstract,
  DefineRepositoryParams
} from './repository.abstract';
import { Observable } from 'rxjs';
import {
  EventParticipationStatus,
  ExternalEventParticipation,
  InternalEventParticipation
} from '@bling-fe/shared/declarations';

type EventParticipationsRepositoryParams = DefineRepositoryParams<void>;

export interface EventParticipationCountsResponse {
  going: number;
  interested: number;
}

type RepositoryEvent = {
  id: number;
  title: string;
  start_at: string;
  end_at: string;
  external_participation_token?: string;
  registration_ends_at?: string | null;
};

interface VerifyTokenResponse {
  event: RepositoryEvent;
}

@Injectable({
  providedIn: 'root'
})
export class EventParticipationsRepository extends RepositoryAbstract<EventParticipationsRepositoryParams> {
  constructor(injector: Injector) {
    super('event_participations', injector);
  }

  /**
   * It joins current user to the event or updates the participation status.
   */
  joinEvent(
    articleId: number,
    status: EventParticipationStatus
  ): Observable<InternalEventParticipation> {
    this.setResourceUrl(articleId);
    return this.http.post<InternalEventParticipation>(
      `${this.RESOURCE_URL}/event_participations/join`,
      { status }
    );
  }

  /**
   * It leaves current user from the event.
   */
  leaveEvent(articleId: number): Observable<LeaveEventResponse> {
    this.setResourceUrl(articleId);
    return this.http.delete<LeaveEventResponse>(
      `${this.RESOURCE_URL}/event_participations/leave`
    );
  }

  /**
   * It removes a participant from an event and returns the id of removed event participation.
   */
  removeParticipant(
    articleId: number,
    id: number
  ): Observable<RemoveParticipantResponse> {
    this.setResourceUrl(articleId);
    return this.http.delete<RemoveParticipantResponse>(
      `${this.RESOURCE_URL}/event_participations/${id}/remove_participant`
    );
  }

  /**
   * It returns the event participations (both internal and external) for a given article id
   * and status.
   */
  findAllByArticleIdAndStatus(
    articleId: number,
    params: { status: EventParticipationStatus }
  ): Observable<FindAllByArticleIdResponse> {
    this.setResourceUrl(articleId);
    return this.http.get<FindAllByArticleIdResponse>(
      `${this.RESOURCE_URL}/event_participations`,
      { params }
    );
  }

  /**
   * It returns counts of going and interested participants for a given article id.
   */
  counts(articleId: number): Observable<EventParticipationCountsResponse> {
    this.setResourceUrl(articleId);
    return this.http.get<EventParticipationCountsResponse>(
      `${this.RESOURCE_URL}/event_participations/counts`
    );
  }

  /**
   * Verifies an external event participation token and returns the associated event.
   */
  verifyToken(token: string): Observable<VerifyTokenResponse> {
    return this.http.get<VerifyTokenResponse>(
      `${this.TENANT_API_URL}/external_event_participations/verify_token`,
      { params: { token } }
    );
  }

  /**
   * Joins an external participant to an event using a token.
   */
  joinExternal(
    token: string,
    participant: {
      first_name: string;
      last_name: string;
      email: string;
    }
  ): Observable<Record<string, never>> {
    return this.http.post<Record<string, never>>(
      `${this.TENANT_API_URL}/external_event_participations/join`,
      {
        token,
        ...participant
      }
    );
  }

  private setResourceUrl(articleId: number) {
    this.RESOURCE_URL = `${this.TENANT_API_URL}/articles/${articleId}`;
  }
}

type FindAllByArticleIdResponse = {
  external: ExternalEventParticipation[];
  internal: InternalEventParticipation[];
};

type RemoveParticipantResponse = {
  id: number;
};

type LeaveEventResponse = {
  id: number;
};
